'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, X, AlertCircle, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

import { usePageBuilderContext } from '../page-builder-context';
import { uploadScreenshot } from '@/lib/services/uploadService';
import type { ImageBlock } from '@/lib/types/page-builder';
import { MaterialSpinner } from '@/components/ui/material-spinner';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface ImageBlockEditorProps {
  block: ImageBlock;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ImageBlockEditor({ block }: ImageBlockEditorProps) {
  const { updateBlockWith, uploads, startUpload, retryUpload, dismissUpload } =
    usePageBuilderContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Skeleton only until the FIRST image renders; after that src swaps happen
  // between decoded images (local preview → cache-warmed server URL), so
  // re-hiding the <img> would only cause flicker
  const [imageLoaded, setImageLoaded] = useState(false);

  const pending = uploads[block.id];
  // The local preview is shown the moment a file is picked; the server URL
  // takes over only after the upload succeeds AND the image is cache-warm
  const preview = pending?.previewUrl ?? block.content.image?.originalUrl ?? null;

  // Optimistic upload: preview renders instantly, the request runs in the
  // background, errors surface inline with a retry
  const handleUpload = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;

    startUpload(block.id, file, uploadScreenshot, (data) => ({
      warmUrl: data.originalUrl,
      commit: () =>
        updateBlockWith(block.id, (b) => ({
          content: { ...(b as ImageBlock).content, image: data },
        })),
    }));
  }, [block.id, startUpload, updateBlockWith]);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleUpload(file);
          toast.info('Изображение вставлено');
        }
        break;
      }
    }
  }, [handleUpload]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onEnter = () => document.addEventListener('paste', handlePaste);
    const onLeave = () => document.removeEventListener('paste', handlePaste);

    container.addEventListener('mouseenter', onEnter);
    container.addEventListener('mouseleave', onLeave);

    return () => {
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleRemove = () => {
    dismissUpload(block.id);
    updateBlockWith(block.id, (b) => ({
      content: { ...(b as ImageBlock).content, image: null },
    }));
  };

  const isUploading = pending?.status === 'uploading';
  const hasError = pending?.status === 'error';

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {preview ? (
        // Image preview - full width, natural aspect ratio, Behance style
        <div className="relative group">
          {!imageLoaded && (
            <div className="w-full aspect-video rounded-lg bg-muted flex items-center justify-center">
              <MaterialSpinner className="h-6 w-6" />
            </div>
          )}
          {/*
            Plain <img>, NOT next/image: the uploaded screenshot is already a
            lossless WebP served via the image proxy. Routing it through
            /_next/image would re-encode it lossy (q=75) and resize it, both of
            which alter pixel colors. Render the proxy URL verbatim so the
            preview is byte-for-byte what the product page will show.
          */}
          <img
            src={preview}
            loading="lazy"
            alt={block.content.alt || 'Uploaded image'}
            className={cn('w-full h-auto', !imageLoaded && 'sr-only')}
            onLoad={() => setImageLoaded(true)}
          />

          {/* Upload in flight — no scrim, just a corner badge so the user
              sees the image and keeps working while it uploads in background */}
          {isUploading && (
            <div className="absolute top-2 right-2 rounded-full bg-card/95 p-2">
              <MaterialSpinner className="h-5 w-5" />
            </div>
          )}

          {/* Upload failed — keep the preview, offer retry in place */}
          {hasError && (
            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3 p-4">
              <div className="flex items-center gap-2 text-white">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Не удалось загрузить</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => retryUpload(block.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-medium rounded-lg"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  Повторить
                </button>
                <button
                  type="button"
                  onClick={() => dismissUpload(block.id)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg backdrop-blur-sm"
                >
                  Отменить
                </button>
              </div>
            </div>
          )}

          {/* Overlay controls on hover */}
          {imageLoaded && !pending && <motion.div
            initial={false}
            animate={{ opacity: isHovered ? 1 : 0 }}
            className="absolute inset-0 bg-black/40 flex items-center justify-center gap-3 pointer-events-none"
          >
            <button
              type="button"
              onClick={() => document.getElementById(`img-input-${block.id}`)?.click()}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg pointer-events-auto backdrop-blur-sm"
            >
              Заменить
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 bg-white/20 hover:bg-destructive text-white rounded-lg pointer-events-auto backdrop-blur-sm"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>}

          <input
            type="file"
            id={`img-input-${block.id}`}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = '';
            }}
            className="sr-only"
          />
        </div>
      ) : (
        // Upload zone - minimal, full width
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById(`img-input-${block.id}`)?.click()}
          className={cn(
            'relative w-full py-16 transition-all duration-200',
            'flex flex-col items-center justify-center gap-3 cursor-pointer',
            isDragging && 'bg-muted/50'
          )}
        >
          <input
            type="file"
            id={`img-input-${block.id}`}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = '';
            }}
            className="sr-only"
          />

          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-foreground-tertiary" />
          </div>
          <div className="text-center">
            <span className="text-sm font-medium text-foreground">
              Нажмите или перетащите изображение
            </span>
            <p className="text-xs text-foreground-tertiary mt-1">
              JPG, PNG или WebP
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
